import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { familiesApi, Family, FamilyMemberCreate } from '../api/families';
import { formatApiError } from '../utils/errorUtils';
import MemberFormModal from '../components/MemberFormModal';
import FamilyNameFormModal from '../components/FamilyNameFormModal';
import ShareInviteModal from '../components/ShareInviteModal';
import ConfirmDialog from '../components/ConfirmDialog';
import MemberCard from '../components/member/MemberCard';
import PageBar, { PageBarMenuItem, PageBarIconAction } from '../components/PageBar';
import { useAuth } from '../contexts/AuthContext';
import './AdminConfig.css';

const emptyMember: FamilyMemberCreate = {
  name: '',
  date_of_birth: '',
  gender: undefined,
  member_relationship: '',
  notes: '',
};

const IconHome = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
  </svg>
);

const IconFolder = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const IconShare = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const FamilyDetail: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [family, setFamily] = useState<Family | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditFamilyModal, setShowEditFamilyModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFamily, setDeletingFamily] = useState(false);

  const loadFamily = useCallback(async () => {
    if (!familyId) return;
    try {
      setFamily(await familiesApi.getFamily(parseInt(familyId, 10)));
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Eroare la incarcarea familiei');
    } finally {
      setLoading(false);
    }
  }, [familyId]);

  useEffect(() => {
    loadFamily();
  }, [loadFamily]);

  // Manual reload kebab item. Wraps loadFamily so the spinner state on
  // the menu item flips while the request is in-flight and any cached
  // staleness in the per-member stats panel gets refreshed.
  const handleReload = useCallback(async () => {
    setReloading(true);
    setError('');
    try {
      await loadFamily();
    } finally {
      setReloading(false);
    }
  }, [loadFamily]);

  const handleDeleteFamily = () => {
    setError('');
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFamily = async () => {
    if (!family) return;
    setDeletingFamily(true);
    setError('');
    try {
      await familiesApi.deleteFamily(family.id);
      setShowDeleteConfirm(false);
      // Pass a one-shot flash via navigation state so the Families
      // list can show a brief confirmation banner after the redirect.
      navigate('/families', {
        state: { flash: `Familia „${family.name}” a fost ștearsă.` },
      });
    } catch (err: any) {
      setError(formatApiError(err.response?.data?.detail) || 'Eroare la ștergerea familiei');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingFamily(false);
    }
  };

  if (loading) {
    return (
      <div className="page-bar-page">
        <PageBar
          ariaLabel="Navigare familie"
          crumbs={[
            { label: 'Dashboard', to: '/dashboard', icon: <IconHome /> },
            { label: 'Familii', to: '/families' },
            { label: 'Se încarcă…' },
          ]}
          backTo="/families"
        />
        <div className="page-container admin-page page-bar-page-content">
          <div className="admin-hero-skeleton" />
          <div className="grid grid-2">
            {[1, 2].map((i) => (
              <div key={i} className="skeleton skeleton-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="page-bar-page">
        <PageBar
          ariaLabel="Navigare familie"
          crumbs={[
            { label: 'Dashboard', to: '/dashboard', icon: <IconHome /> },
            { label: 'Familii', to: '/families' },
            { label: 'Necunoscută' },
          ]}
          backTo="/families"
        />
        <div className="page-container page-bar-page-content">
          <div className="alert alert-error">Familie negăsită</div>
        </div>
      </div>
    );
  }

  const isOwner = Boolean(user && family.owner_id === user.id);

  // Edit + Share moved out of the kebab and onto the bar as inline
  // icon buttons (right of "Adaugă membru"). The kebab keeps the
  // less-common Reîncarcă + (owner-only) Șterge familia verbs.
  const iconActions: PageBarIconAction[] = [
    {
      icon: <IconEdit />,
      label: 'Editează familia',
      onClick: () => setShowEditFamilyModal(true),
    },
  ];
  if (isOwner) {
    iconActions.push({
      icon: <IconShare />,
      label: 'Partajare',
      onClick: () => setShowShareModal(true),
    });
  }

  const menuItems: PageBarMenuItem[] = [
    {
      label: 'Reîncarcă',
      icon: <IconRefresh />,
      onClick: handleReload,
      disabled: reloading,
      busy: reloading,
      busyLabel: 'Se reîncarcă…',
    },
  ];
  if (isOwner) {
    menuItems.push({
      label: 'Șterge familia',
      icon: <IconTrash />,
      onClick: handleDeleteFamily,
      danger: true,
      disabled: deletingFamily,
      busy: deletingFamily,
      busyLabel: 'Se șterge…',
    });
  }

  return (
    <div className="page-bar-page">
      <PageBar
        ariaLabel="Navigare familie"
        crumbs={[
          { label: 'Dashboard', to: '/dashboard', icon: <IconHome /> },
          { label: 'Familii', to: '/families' },
          { label: family.name },
        ]}
        backTo="/families"
        leadingChip={
          <span
            className="page-bar-crumb-chip"
            style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' }}
            aria-hidden
          >
            <IconFolder />
          </span>
        }
        primaryAction={{
          label: 'Adaugă membru',
          icon: <IconPlus />,
          onClick: () => setShowAddMemberModal(true),
        }}
        iconActions={iconActions}
        menuItems={menuItems}
      />

      <div className="page-container admin-page page-bar-page-content">
        {error && <div className="alert alert-error">{error}</div>}

        <FamilyNameFormModal
          open={showEditFamilyModal}
          onClose={() => setShowEditFamilyModal(false)}
          title="Editează familia"
          initialName={family.name}
          submitLabel="Salvează"
          onSubmit={async (name) => {
            await familiesApi.updateFamily(family.id, { name });
            await loadFamily();
          }}
        />

        {isOwner && (
          <ShareInviteModal
            open={showShareModal}
            onClose={() => setShowShareModal(false)}
            kind="family"
            familyId={family.id}
          />
        )}

        {isOwner && (
          <ConfirmDialog
            open={showDeleteConfirm}
            title="Șterge familia"
            variant="danger"
            confirmLabel="Șterge"
            cancelLabel="Anulează"
            busy={deletingFamily}
            onConfirm={confirmDeleteFamily}
            onCancel={() => {
              if (!deletingFamily) setShowDeleteConfirm(false);
            }}
            message={
              <>
                <p style={{ margin: 0 }}>
                  Ștergi definitiv familia <strong>{family.name}</strong>?
                </p>
                <p style={{ margin: 0, color: 'var(--color-slate-600)' }}>
                  Toți membrii, documentele și partajările active vor fi șterse
                  permanent. Acțiunea nu poate fi anulată.
                </p>
              </>
            }
          />
        )}

        <MemberFormModal
          open={showAddMemberModal}
          onClose={() => setShowAddMemberModal(false)}
          title="Membru nou"
          submitLabel="Adaugă"
          initial={emptyMember}
          onSubmit={async (data) => {
            if (!familyId) return;
            await familiesApi.createMember(parseInt(familyId, 10), {
              ...data,
              date_of_birth: data.date_of_birth || undefined,
            });
            await loadFamily();
          }}
        />

        {family.members.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <div className="empty-state-title">Niciun membru</div>
              <div className="empty-state-text">Adaugă primul membru al familiei.</div>
              <button type="button" className="btn btn-primary" onClick={() => setShowAddMemberModal(true)}>
                <IconUsers />
                Adaugă membru
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-2">
            {family.members.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyDetail;
